<template>
  <div class="min-h-screen">
    <div v-if="engagement">

      <ContextBar v-if="engagement.entity">
        <font-awesome-icon :icon="entityIcon" class="w-3.5 h-3.5" />
        <span v-if="engagement.entity_type_name" class="text-gray-800 font-semibold">{{ engagement.entity_type_name }}:</span>
        <CC_Router_Link_To_Entity
          :params="{
            data: { id: engagement.entity, name: engagement.entity_name },
            hideIcon: true,
          }" />
      </ContextBar>

      <PageHeader
        :title="pageHeaderTitle"
        :breadcrumbs="pageHeaderBreadcrumbs">
  
        <TeamsMeetingSelector
          :engagement-id="engagement.id"
          :engagement-date="engagement.date"
          class="inline-block"
        />
        
      </PageHeader>

      <!-- Main Content Area -->
      <div class="max-w-7xl mx-auto px-8 lg:px-16 py-8">
        <!-- Main Card -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
          <ComponentHeader title="Engagement Overview" :icon="['fas', 'info-circle']" icon-class="text-primary-600" />
          <div class="p-8">
            <div class="grid grid-cols-1 lg:grid-cols-5 gap-8">
              <!-- Left Column - Engagement Details -->
              <div class="lg:col-span-2 space-y-6">
                <div class="space-y-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      Partner <span class="text-red-500">*</span>
                    </label>

                    <div>
                        <div v-if="!engagement.entity">
                          <EntitySearch_Single
                            buttonText="Select Entity"
                            modalTitle="Select Entity"
                            @entity-selected="onEntitySelected"
                          />
                        </div>
                        <div v-else class="mt-3 p-4 bg-primary-50 rounded-lg border border-primary-200 justify-between">
                          <router-link
                            :to="{ name: 'Entity', params: { entity_id: engagement.entity } }"
                            class="btn-link mb-2"
                          >
                            <span class="sensitive">{{ engagement.entity_name }}</span>
                          </router-link>
                          <EntitySearch_Single
                            buttonText="Change Entity"
                            modalTitle="Select New Entity"
                            @entity-selected="onEntitySelected"
                          />
                        </div>
                    </div>
                  </div>

                  <!-- Channel Selection -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      Channel
                    </label>
                    <select v-model="engagement.engagement_channel"
                            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent transition-all">
                      <option v-for="type in filteredEngagementChannels" :key="type.id" :value="type.id">
                        {{type.name}}
                      </option>
                    </select>
                  </div>

                  <!-- Type Selection -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      Type
                    </label>
                    <select v-model="engagement.engagement_type"
                            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent transition-all">
                      <option v-for="type in engagement_types" :key="type.id" :value="type.id">
                        {{type.name}}
                      </option>
                    </select>
                  </div>

                  <!-- Topics Multi-Select -->
                  <EngagementTopicSelector
                    v-if="engagement_topics.length > 0 && engagement.engagement_topics !== undefined"
                    v-model="engagement.engagement_topics"
                    :topics="engagement_topics"
                  />

                  <!-- Status Selection -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select v-model="engagement.status"
                            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent transition-all">
                      <option v-for="status in engagement_statuses" :key="status.id" :value="status.id">
                        {{status.name}}
                      </option>
                    </select>
                  </div>

                  <!-- Date Selection -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">
                      Date
                    </label>
                    <input v-model="engagement.date"
                           type="date"
                           class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent transition-all" />
                  </div>
                </div>
              </div>
              <!-- Right Column - Subject and Notes -->
              <div class="lg:col-span-3 space-y-6">
                <!-- Subject Input -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Subject
                  </label>
                  <input v-model="engagement.subject"
                         placeholder="Enter engagement subject..."
                         class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent transition-all" />
                </div>

                <!-- Notes Textarea -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea v-model="engagement.note"
                            rows="12"
                            placeholder="Enter engagement notes..."
                            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent transition-all resize-none"></textarea>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Transcript Section -->
        <div class="mt-6 bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
          <ComponentHeader title="Meeting Transcript" :icon="['fas', 'file-alt']" icon-class="text-purple-600" />

          <div class="p-6 space-y-4">
            <!-- Success/Error Message -->
            <div v-if="transcriptFetchMessage" 
                 :class="[
                   'p-4 rounded-lg border',
                   transcriptFetchSuccess 
                     ? 'bg-green-50 border-green-200 text-green-800' 
                     : 'bg-red-50 border-red-200 text-red-800'
                 ]">
              <div class="flex items-center">
                <font-awesome-icon 
                  :icon="['fas', transcriptFetchSuccess ? 'check-circle' : 'exclamation-triangle']" 
                  class="mr-2" />
                <span class="font-medium">{{ transcriptFetchMessage }}</span>
              </div>
            </div>

            <div v-if="engagement.zoomMeetingTranscription" class="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
              <span class="font-medium text-gray-900">Full Transcript Available</span>
              <div class="flex gap-2">
                <button @click="transcriptModalOpen=true"
                        class="btn-primary-sm">
                  <font-awesome-icon :icon="['fas', 'external-link-alt']" />
                  Open Transcript
                </button>
                <button @click="replaceTranscriptModalOpen = true"
                        class="btn-danger-sm">
                  <font-awesome-icon :icon="['fas', 'sync-alt']" />
                  Replace Transcript
                </button>
              </div>
            </div>
            <div v-if="engagement.engagement_transcription_summary">
              <Transcript_Summary :transcript-summary="engagement.engagement_transcription_summary"
                                @update="updateTranscriptSummary" />
            </div>
            <div v-if="!engagement.zoomMeetingTranscription && !engagement.transcription_summary && !transcriptFetchMessage" class="flex items-center justify-center gap-4 p-8">
              <button v-if="isTenantVytalize" 
                      @click="fetchTranscript"
                      :disabled="fetchingTranscript"
                      class="btn-secondary">
                <font-awesome-icon 
                  :icon="['fas', fetchingTranscript ? 'spinner' : 'download']" 
                  :class="['mr-2', { 'fa-spin': fetchingTranscript }]" />
                {{ fetchingTranscript ? 'Searching...' : 'Search Microsoft for Transcription' }}
              </button>
              <button @click="uploadTranscriptModalOpen = true"
                      class="btn-primary">
                <font-awesome-icon :icon="['fas', 'upload']" />
                Upload Transcript
              </button>
            </div>

            <!-- Summarize Transcript Button -->
            <div v-if="engagement.zoomMeetingTranscription && !engagement.engagement_transcription_summary" class="flex items-center justify-center p-4">
              <button @click="summarizeTranscript"
                      :disabled="summarizingTranscript"
                      :class="[
                        'inline-flex items-center px-6 py-3 text-sm font-medium rounded-lg transition-all duration-200 shadow-sm transform',
                        summarizingTranscript 
                          ? 'text-gray-400 bg-gray-200 cursor-not-allowed' 
                          : 'text-white bg-primary-500 hover:bg-primary-600 hover:scale-105'
                      ]">
                <font-awesome-icon 
                  :icon="['fas', summarizingTranscript ? 'spinner' : 'magic']" 
                  :class="['mr-2', { 'fa-spin': summarizingTranscript }]" />
                {{ summarizingTranscript ? 'Summarizing...' : 'Summarize Transcript' }}
              </button>
            </div>

            <!-- Transcript Summary Error -->
            <div v-if="transcriptSummaryError"
                 class="p-4 rounded-lg border bg-red-50 border-red-200 text-red-800">
              <div class="flex items-center">
                <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="mr-2" />
                <span class="font-medium">{{ transcriptSummaryError }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Attachments Section -->
        <div class="mt-6 bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
          <ComponentHeader title="Attachments" :icon="['fas', 'paperclip']" icon-class="text-blue-600" />

          <div class="p-6">
            <!-- Upload Component -->
            <EngagementMediaUploader
              v-if="engagement?.id"
              :engagement-id="engagement.id"
              @file-uploaded="onFileUploaded"
            />

            <!-- Attachments List -->
            <div v-if="attachments.length > 0" class="mt-6 space-y-3">
              <div v-for="attachment in attachments" :key="attachment.id"
                   class="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all duration-200">
                <div class="flex items-center">
                  <font-awesome-icon :icon="getFileIcon(attachment.filename)" class="mr-3 text-gray-600 text-lg" />
                  <div>
                    <span class="sensitive text-sm font-medium text-gray-900">{{ attachment.filename }}</span>
                    <div class="text-xs text-gray-500">
                      {{ formatFileSize(attachment.file_size) }} &bull;
                      Uploaded {{ formatDate(attachment.created_at) }}
                      <span v-if="attachment.uploaded_by_name"> by <span class="sensitive">{{ attachment.uploaded_by_name }}</span></span>
                    </div>
                  </div>
                </div>
                <div class="flex gap-2">
                  <button @click="downloadAttachment(attachment)"
                          class="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-all duration-200">
                    <font-awesome-icon :icon="['fas', 'download']" class="mr-1" />
                    Download
                  </button>
                  <button @click="confirmDeleteAttachment(attachment)"
                          class="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-all duration-200">
                    <font-awesome-icon :icon="['fas', 'trash']" class="mr-1" />
                    Delete
                  </button>
                </div>
              </div>
            </div>

            <p v-else-if="!attachmentsLoading" class="text-gray-500 text-sm mt-4 text-center py-4">
              No attachments yet. Upload meeting documents, presentations, or PDFs above.
            </p>

            <div v-if="attachmentsLoading" class="flex justify-center py-4">
              <font-awesome-icon :icon="['fas', 'spinner']" class="fa-spin text-gray-400" />
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="mt-6 flex items-center gap-3 flex-wrap">
          <button @click="updateEngagement"
                  class="btn-primary">
            <font-awesome-icon :icon="['fas', 'save']" />
            Save Changes
          </button>

          <div v-if="loading"
               class="inline-flex items-center px-6 py-3 text-sm font-medium text-white bg-green-dark rounded-lg shadow-sm">
            <font-awesome-icon :icon="['fas', 'spinner']" class="fa-spin" />
            Processing...
          </div>

          <button v-else-if="engagement.entity"
                  @click.prevent="parseEngagement"
                  class="btn-primary">
            <font-awesome-icon :icon="['fas', 'cogs']" />
            Parse Content
          </button>

          <button v-if="engagement.entity"
                  @click.prevent="deleteParsedContentModalOpen = !deleteParsedContentModalOpen"
                  class="btn-danger">
            <font-awesome-icon :icon="['fas', 'trash-alt']" />
            Delete Parsed Content
          </button>

          <div v-else
               class="btn-disabled">
            <font-awesome-icon :icon="['fas', 'info-circle']" />
            Select Entity
          </div>

          <button @click.prevent="discardEngagement"
                  class="btn-secondary">
            <font-awesome-icon :icon="['fas', 'times']" />
            Discard
          </button>

          <div v-if="lastAutoSaveTime" class="ml-auto flex items-center text-sm text-gray-500">
            <font-awesome-icon :icon="['fas', 'check-circle']" class="mr-1 text-green-500" />
            Auto-saved at {{ lastAutoSaveTime }}
          </div>
        </div>

        <!-- Field Errors -->
        <div v-if="engagement_field_errors.length > 0" class="mt-4">
          <div class="bg-red-50 border border-red-200 rounded-lg p-4">
            <div class="flex">
              <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="text-red-600 mr-2 shrink-0 mt-0.5" />
              <div class="flex-1">
                <Field_Errors :received_errors="engagement_field_errors"/>
              </div>
            </div>
          </div>
        </div>

        <!-- Participants Grid -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8" :key="tag_update_key">
          <!-- Engagement Participants -->
          <div class="col-span-2 bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
            <ComponentHeader title="Engagement Participants" :icon="['fas', 'users']" icon-class="text-primary-600">
              <EntitySearch_Single
                buttonText="Add Staff"
                modalTitle="Select Staff Member"
                filterEntityTypeName="Staff"
                @entity-selected="onParticipantEntitySelected"
              />
            </ComponentHeader>

            <div class="p-6">
              <!-- Participants Table (when participants exist) -->
              <template v-if="engagement.engagement_participants?.length > 0">
                <!-- Participants Table Header -->
                <div class="px-4 grid grid-cols-5 gap-4 pb-3 border-b border-gray-200 text-sm font-medium text-gray-600">
                  <div>Partner Name</div>
                  <div class="text-center">Role</div>
                  <div class="text-center">Engagement</div>
                  <div class="text-center">Meeting Time</div>
                  <div class="text-center">Actions</div>
                </div>

                <!-- Participants List -->
                <div class="space-y-3 mt-4">
                  <div v-for="participant in engagement.engagement_participants"
                       :key="participant.object_id"
                       class="group grid grid-cols-5 gap-4 items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all duration-200">
                    <div class="sensitive text-sm font-semibold text-gray-900">
                      {{participant.name || 'Unknown'}}
                    </div>
                    <div class="text-center text-sm text-gray-600">
                      {{participant.role || '-'}}
                    </div>
                    <div class="flex justify-center">
                      <span class="px-3 py-1 rounded-full text-xs font-small text-white shadow-sm"
                            :style="{ backgroundColor: getEngagementLevelColor(participant.engagement_score) }">
                        {{getEngagementText(participant.engagement_score)}}
                      </span>
                    </div>
                    <div class="text-center text-gray-600 text-sm">
                      {{participant.attendance_seconds ? Math.round(participant.attendance_seconds / 60) : 0}} min
                    </div>
                    <div class="flex justify-center">
                      <button @click="openDeleteEngagementParticipantModal(participant.id)"
                              class="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200">
                        <font-awesome-icon :icon="['fas', 'trash']" />
                      </button>
                    </div>
                  </div>
                </div>
              </template>

              <!-- Empty state (when no participants) -->
              <div v-else class="text-center py-8">
                <font-awesome-icon :icon="['fas', 'users']" class="text-4xl text-gray-300 mb-3" />
                <p class="text-gray-500">No participants recorded</p>
                <p class="text-sm text-gray-400 mt-1">Click "Add Staff" to add participants</p>
              </div>
            </div>
          </div>

          <!-- Team Member Participants -->
          <div v-if="engagement.parsed_engagement_structure?.employee_meeting_participants"
               class="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
            <ComponentHeader title="Team Member Participants" />

            <div class="p-6">
              <div class="space-y-3">
                <div v-for="participant in engagement.parsed_engagement_structure?.employee_meeting_participants"
                     :key="participant.name"
                     class="p-4 bg-primary-50 rounded-lg hover:from-purple-100 hover:to-primary-100 transition-all duration-200">
                  <div class="flex items-center">
                    <font-awesome-icon :icon="['fas', 'user']" class="mr-3 text-purple-600" />
                    <span class="sensitive font-medium text-gray-900">{{participant.name}}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Key Meeting Takeaway -->
        <div v-if="engagement.parsed_engagement_structure?.key_meeting_takeaway"
             class="mt-8 bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
          <ComponentHeader title="Key Takeaway" :icon="['fas', 'lightbulb']" icon-class="text-amber-500" />
          <div class="p-6">
            <p class="sensitive text-gray-700 leading-relaxed" data-demo-blur>
              {{ engagement.parsed_engagement_structure.key_meeting_takeaway }}
            </p>
          </div>
        </div>

        <!-- Thread Summary -->
        <div v-if="engagement.parsed_engagement_structure?.partner_meeting_participants"
             class="mt-8 bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
          <ComponentHeader title="Thread Summary" :icon="['fas', 'comments']" icon-class="text-green-600" />
          <div class="p-6">
            <Details_Subthreads :engagement_id="engagement.id" :key="tag_update_key" />
          </div>
        </div>

        <!-- Follow-Up Actions -->
        <div v-if="engagement.parsed_engagement_structure?.action_items_discussed"
             class="mt-8 mb-24 bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
          <ComponentHeader title="Follow-Up Actions" :icon="['fas', 'tasks']" icon-class="text-orange-600" />
          <div class="p-6">
            <Action_Table
              :shadow="true"
              :theme="standardListThemeRounded"
              :sql-filters="{
              'originating_object_id': engagement.id,
              'originating_object_content_type_model': 'engagement',
            }" />
          </div>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <transition
      enter-active-class="transition ease-out duration-300"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-200"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div v-if="transcriptModalOpen" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div class="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden transform transition-all">
          <Transcript_Modal :transcription_text="engagement.zoomMeetingTranscription"
                           @close="transcriptModalOpen=false" />
        </div>
      </div>
    </transition>

    <!-- Upload Transcript Modal -->
    <transition name="fade">
      <div v-if="uploadTranscriptModalOpen" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div class="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden">
          <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 class="text-lg font-semibold text-gray-900 flex items-center">
              <font-awesome-icon :icon="['fas', 'upload']" class="mr-2 text-blue-600" />
              Upload Transcript
            </h3>
            <button @click="closeUploadTranscriptModal"
                    class="text-gray-400 hover:text-gray-600 transition-colors">
              <font-awesome-icon :icon="['fas', 'times']" />
            </button>
          </div>

          <div class="p-6">
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Paste Transcript
                </label>
                <textarea v-model="uploadTranscriptText"
                          rows="15"
                          placeholder="Paste the meeting transcript here..."
                          class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent transition-all resize-none"></textarea>
              </div>

              <div v-if="uploadTranscriptError" class="p-4 rounded-lg border bg-red-50 border-red-200 text-red-800">
                <div class="flex items-center">
                  <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="mr-2" />
                  <span class="font-medium">{{ uploadTranscriptError }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
            <button @click="closeUploadTranscriptModal"
                    class="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-200">
              <font-awesome-icon :icon="['fas', 'times']" class="mr-2" />
              Cancel
            </button>
            <button @click="submitUploadTranscript"
                    :disabled="uploadingTranscript || !uploadTranscriptText.trim()"
                    :class="[
                      'inline-flex items-center px-6 py-2 text-sm font-medium rounded-lg transition-all duration-200 shadow-sm',
                      uploadingTranscript || !uploadTranscriptText.trim()
                        ? 'text-gray-400 bg-gray-200 cursor-not-allowed'
                        : 'text-white bg-primary-500 hover:bg-primary-600'
                    ]">
              <font-awesome-icon 
                :icon="['fas', uploadingTranscript ? 'spinner' : 'check']" 
                :class="['mr-2', { 'fa-spin': uploadingTranscript }]" />
              {{ uploadingTranscript ? 'Uploading...' : 'Upload' }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <ConfirmationModal
      :is-visible="deleteParsedContentModalOpen"
      :message="'Are you sure you want to delete all parsed content?'"
      @confirm="delete_parsed_content"
      @cancel="deleteParsedContentModalOpen = !deleteParsedContentModalOpen"
    />

    <ConfirmationModal
      :is-visible="deleteEngagementParticipantModalOpen"
      :message="'Are you sure you want to delete this engagement participant?'"
      @confirm="deleteEngagementParticipant"
      @cancel="deleteEngagementParticipantModalOpen = !deleteEngagementParticipantModalOpen"
    />

    <ConfirmationModal
      :is-visible="deleteAttachmentModalOpen"
      :message="'Are you sure you want to delete this attachment?'"
      @confirm="deleteAttachment"
      @cancel="deleteAttachmentModalOpen = false"
    />

    <ConfirmationModal
      :is-visible="replaceTranscriptModalOpen"
      :message="'This will permanently remove the current transcript, summary, and all parsed content. You can then upload a new transcript.'"
      @confirm="handleClearTranscript"
      @cancel="replaceTranscriptModalOpen = false"
    />
  </div>
</template>

<script>
import {defineComponent, nextTick} from 'vue'
import axios from 'axios'
import router from '../../../router'
import Action_Table from '@/components/Lists/Action_Table.vue'
import Transcript_Modal from "@/components/widgetcomponents/JSON_Editors/Transcript_Modal.vue"
import {standardListThemeRounded, toProperCase} from "@/utils/common"
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome"
import EntitySearch_Single from "@/components/widgetcomponents/Search_Widgets/EntitySearch_Single.vue"
import Field_Errors from "@/components/widgetcomponents/Field_Errors.vue"
import Details_Subthreads from "@/components/Details/Details_Subthreads.vue"
import ConfirmationModal from "@/components/widgetcomponents/ConfirmationModal.vue"
import Transcript_Summary from "@/components/widgetcomponents/JSON_Editors/Transcript_Summary.vue"
import TeamsMeetingSelector from "@/components/widgetcomponents/TeamsMeetingSelector.vue"
import JsonEditor from "@/components/widgetcomponents/JSON_Editors/JSON_Editor.vue"
import EngagementMediaUploader from "@/components/widgetcomponents/EngagementMediaUploader.vue"
import { clearTranscript } from '@/services/api/engagements'
import EngagementTopicSelector from "@/components/widgetcomponents/EngagementTopicSelector.vue"
import spiralDots from '@/assets/spiral_dots.svg'
import PageHeader from '../widgetcomponents/library/PageHeader.vue'
import ComponentHeader from '../widgetcomponents/library/ComponentHeader.vue'
import CC_Router_Link_To_Entity from '../widgetcomponents/AG_Grid_Custom_Cells/CC_Router_Link_To_Entity.vue'
import ContextBar from '../widgetcomponents/library/ContextBar.vue'
import { getEntityIcon } from '@/utils/entityIcons'

export default defineComponent({
  components: {
    JsonEditor,
    TeamsMeetingSelector,
    Transcript_Summary,
    ConfirmationModal,
    Details_Subthreads,
    Field_Errors,
    EntitySearch_Single,
    FontAwesomeIcon,
    Transcript_Modal,
    Action_Table,
    EngagementMediaUploader,
    EngagementTopicSelector,
    PageHeader,
    ComponentHeader,
    CC_Router_Link_To_Entity,
    ContextBar
  },
  props:['engagement_id'],
  data(){
    return{
      engagement_key:0,
      engagement:null,
      tag_update_key:0,
      available_options:{},
      engagement_types:[],
      engagement_channels:[],
      engagement_topics:[],
      engagement_field_errors:[],
      engagement_statuses:[
        {
          name:'Scheduled',
          id:'SCHEDULED'
        },
        {
          name:'Completed',
          id:'COMPLETED'
        },
        {
          name:'Cancelled',
          id:'CANCELLED'
        },
        {
          name:'Rescheduled',
          id:'RESCHEDULED'
        },
        {
          name:'No Show',
          id:'NO_SHOW'
        },
      ],
      loading:false,
      autoSaveInterval: null,
      isDiscarded: false,
      transcriptModalOpen:false,
      backgroundImage: spiralDots,
      json_transcript:{},
      transcript_options:[],
      selected_transcript_option: null,
      deleteParsedContentModalOpen:false,
      replaceTranscriptModalOpen: false,
      deleteEngagementParticipantModalOpen:false,
      participantIdToDelete: null,
      lastAutoSaveTime: null,
      transcript_summary_edit_open:false,
      fetchingTranscript: false,
      transcriptFetchMessage: null,
      transcriptFetchSuccess: false,
      summarizingTranscript: false,
      transcriptSummaryError: null,
      attachments: [],
      attachmentsLoading: false,
      attachmentToDelete: null,
      deleteAttachmentModalOpen: false,
      uploadTranscriptModalOpen: false,
      uploadTranscriptText: '',
      uploadingTranscript: false,
      uploadTranscriptError: null
    }
  },
  computed: {
    pageHeaderTitle() {
      if (!this.engagement) return 'Engagement Details'
      return this.engagement.entity_name
        ? `${this.engagement.date} with ${this.engagement.entity_name}`
        : `Engagement on ${this.engagement.date}`
    },
    pageHeaderBreadcrumbs() {
      return [
        { label: 'Engagement' },
      ]
    },
    standardListThemeRounded() {
      return standardListThemeRounded
    },
    isTenantVytalize() {
      return this.$store.getters['tenant/current_tenant_slug'] === 'vytalize'
    },
    isTenantDiverge() {
      return this.$store.getters['tenant/current_tenant_slug'] === 'diverge'
    },
    entityIcon() {
      return getEntityIcon(this.engagement)
    },
    filteredEngagementChannels() {
      if (this.isTenantDiverge) {
        const allowedChannelIds = [1, 2, 3, 7]
        return this.engagement_channels.filter(channel => allowedChannelIds.includes(channel.id))
      }
      return this.engagement_channels
    }

  },
  methods:{
    getEngagementLevelColor(level) {
      switch(level) {
        case 1:
          return '#10B981' // Modern emerald
        case 0.75:
          return '#34D399' // Light emerald
        case 0.5:
          return '#FCD34D' // Modern amber
        case 0.25:
          return '#FB923C' // Modern orange
        case 0.0:
          return '#EF4444' // Modern red
        default:
          return '#9CA3AF' // Modern gray
      }
    },
    getEngagementText(engagement_score) {
      switch(engagement_score) {
        case 1:
          return 'Very Engaged'
        case 0.75:
          return 'Engaged'
        case 0.5:
          return 'Indifferent'
        case 0.25:
          return 'Disengaged'
        case 0.0:
          return 'Hostile'
        default:
          return 'Unknown'
      }
    },
    toProperCase,
    async discardEngagement() {
      this.isDiscarded = true
      try {
        await axios.delete(`/api/engagements/${this.engagement.id}/`)
        router.push({ name: 'Home' })
      } catch (error) {
        console.error('Error discarding engagement:', error)
      }
    },
    update(){
      this.fetchEngagementDetails()
    },
    updateEngagement(){
      this.engagement_field_errors=[]
      const updateData = {
        ...this.engagement
      }
      delete updateData.zoomMeetingTranscription
      delete updateData.transcription_summary
      delete updateData.transcription_summary_status
      delete updateData.parsed_engagement_structure
      delete updateData.engagement_topic_names

      return axios.patch(`/api/engagements/${this.engagement.id}/`, updateData)
        .then((response) => {
          this.engagement = response.data
          this.lastAutoSaveTime = new Date().toLocaleTimeString()

          // Handle transcript summary completion
          if (this.summarizingTranscript) {
            if (this.engagement.transcription_summary_status === 'COMPLETED') {
              this.summarizingTranscript = false
              this.transcriptSummaryError = null
            } else if (this.engagement.transcription_summary_status === 'FAILED') {
              this.summarizingTranscript = false
              this.transcriptSummaryError = 'Failed to summarize transcript'
            }
          }
        })
        .catch(error => {
          console.error('Error updating engagement:', error)
          this.engagement_field_errors=[`Error updating engagement, please email support@kaironhealth.com: ${error}`]
        })
    },
    updateTranscriptSummary(new_summary){
      const updateData = {
        transcription_summary:JSON.stringify(new_summary)
      }

      return axios.patch(`/api/engagements/${this.engagement.id}/`, updateData)
        .then((response) => {
          this.engagement = response.data
        })
        .catch(error => {
          console.error('Error updating engagement:', error)
          this.engagement_field_errors=[`Error updating engagement, please email support@kaironhealth.com: ${error}`]
        })
    },
    fetchEngagementChannels(){
      axios.get('/api/engagementChannels/')
        .then(response=>{
          this.engagement_channels=response.data
        })
        .catch(error => {
          console.error('Error fetching engagement types: ', error)
        })
    },
    fetchEngagementTypes(){
      axios.get('/api/engagementtypes/')
        .then(response=>{
          this.engagement_types=response.data
        })
        .catch(error => {
          console.error('Error fetching engagement types: ', error)
        })
    },
    fetchEngagementTopics(){
      axios.get('/api/engagementtopics/')
        .then(response=>{
          this.engagement_topics=response.data
        })
        .catch(error => {
          console.error('Error fetching engagement topics: ', error)
        })
    },
    onParticipantEntitySelected(entity) {
      axios.post('api/engagementparticipants/', {
          engagement: this.engagement.id,
          participant: entity.id,
          attendance_seconds: 0,
          engagement_score: null,
      })
      .then(() => {
        this.fetchEngagementDetails()
      })
      .catch(error => {
        console.error('Error adding engagement participant:', error)
      })
    },
    parseEngagement() {
      if (this.engagement.entity &&
          (this.engagement?.note?.length > 20 ||
          this.engagement?.zoomMeetingTranscription?.length > 20)){
        this.loading = true
        axios.post(`/api/engagements/${this.engagement.id}/parse/`)
          .then(() => {
            this.fetchEngagementDetails()
            this.$emit('update')
          })
          .catch(error => {
            console.error('Error parsing engagements', error)
          }).finally(() => {
            this.tag_update_key+=1
            this.loading = false
          })
      }
    },
    delete_parsed_content() {
      if (this.engagement_id){
        this.loading = true
        axios.post(`/api/engagements/${this.engagement.id}/delete_parsed_content/`)
          .then(() => {
            this.$emit('update')
          })
          .catch(error => {
            console.error('Error parsing engagements', error)
          }).finally(() => {
            this.tag_update_key+=1
            this.deleteParsedContentModalOpen = false
            this.engagement.parsed_engagement_structure = null
            this.loading = false
          })
      }
    },
    fetchEngagementDetails() {
      axios.get(`/api/engagements/${this.engagement_id}/`)
        .then(response => {
          this.engagement = response.data
        })
        .catch((error) => {
          const { status } = error.response
          if (status === 403) {
            router.push({ name: 'ForbiddenPage' })
          } else if (status === 404) {
            router.push({ name: 'NotFound' })
          } else if (status >= 500) {
            router.push({ name: 'ErrorPage' })
          }
          console.error('Error fetching engagement:', error)
        })
    },
    onEntitySelected(entity) {
      this.engagement.entity = entity.id
      this.engagement.entity_name = entity.name
    },
    parse_json_summary() {
      if (!this.engagement?.transcription_summary) {
        this.json_transcript = {}
        this.transcript_options = []
        return
      }

      try {
        const parsedJson = JSON.parse(this.engagement.transcription_summary)
        this.json_transcript = parsedJson
        this.transcript_options = Object.keys(parsedJson)

        if (this.transcript_options.length > 0) {
          this.selected_transcript_option = this.transcript_options[0]
        }
      } catch (error) {
        console.log("Could not parse transcription summary as JSON:", error.message)
        this.json_transcript = {}
        this.transcript_options = []
      }
    },
    deleteEngagementParticipant() {
      axios.delete(`/api/engagementparticipants/${this.participantIdToDelete}/`)
        .then(() => {
          this.fetchEngagementDetails()
          this.deleteEngagementParticipantModalOpen = false
        })
        .catch(error => {
          console.error('Error deleting engagement participant:', error)
        })
    },
    openDeleteEngagementParticipantModal(participantId) {
      this.deleteEngagementParticipantModalOpen = true
      this.participantIdToDelete = participantId
    },
    fetchTranscript() {
      this.fetchingTranscript = true
      this.transcriptFetchMessage = null
      this.transcriptFetchSuccess = false
      axios.get(`/api/engagements/${this.engagement_id}/transcript/`)
        .then(() => {
          this.fetchEngagementDetails()
          this.transcriptFetchMessage = 'Transcript search completed successfully. Press Parse Content to analyze.'
          this.transcriptFetchSuccess = true
        })
        .catch(() => {
          this.transcriptFetchMessage = 'No transcript found.'
          this.transcriptFetchSuccess = false
        })
        .finally(() => {
          this.fetchingTranscript = false
        })
    },
    summarizeTranscript() {
      this.summarizingTranscript = true
      this.transcriptSummaryError = null
      axios.post(`/api/engagements/${this.engagement_id}/summarize_transcript/`)
        .catch(error => {
          console.error('Error summarizing transcript:', error)
          this.transcriptSummaryError = error.response?.data?.message || error.message
          this.summarizingTranscript = false
        })
    },
    // Attachment methods
    async fetchAttachments() {
      if (!this.engagement_id) return
      this.attachmentsLoading = true
      try {
        const response = await axios.get('/api/engagementmedia/', {
          params: { engagement: this.engagement_id }
        })
        this.attachments = response.data
      } catch (error) {
        console.error('Error fetching attachments:', error)
      } finally {
        this.attachmentsLoading = false
      }
    },
    onFileUploaded(fileData) {
      this.fetchAttachments()
    },
    async downloadAttachment(attachment) {
      try {
        const response = await axios.get(`/api/engagementmedia/${attachment.id}/download/`)
        if (response.data.url) {
          window.open(response.data.url, '_blank')
        }
      } catch (error) {
        console.error('Error downloading attachment:', error)
      }
    },
    confirmDeleteAttachment(attachment) {
      this.attachmentToDelete = attachment
      this.deleteAttachmentModalOpen = true
    },
    async deleteAttachment() {
      if (!this.attachmentToDelete) return
      try {
        await axios.delete(`/api/engagementmedia/${this.attachmentToDelete.id}/`)
        this.fetchAttachments()
      } catch (error) {
        console.error('Error deleting attachment:', error)
      } finally {
        this.attachmentToDelete = null
        this.deleteAttachmentModalOpen = false
      }
    },
    async handleClearTranscript() {
      this.replaceTranscriptModalOpen = false
      try {
        await clearTranscript(this.engagement.id)
        await this.fetchEngagementDetails()
      } catch (error) {
        console.error('Error clearing transcript:', error)
      }
    },
    getFileIcon(filename) {
      const ext = filename?.split('.').pop()?.toLowerCase()
      if (['doc', 'docx'].includes(ext)) return ['fas', 'file-word']
      if (ext === 'pdf') return ['fas', 'file-pdf']
      if (['ppt', 'pptx'].includes(ext)) return ['fas', 'file-powerpoint']
      if (['jpg', 'jpeg', 'png'].includes(ext)) return ['fas', 'file-image']
      return ['fas', 'file']
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 B'
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    },
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    },
    closeUploadTranscriptModal() {
      this.uploadTranscriptModalOpen = false
      this.uploadTranscriptText = ''
      this.uploadTranscriptError = null
    },
    async submitUploadTranscript() {
      if (!this.uploadTranscriptText.trim()) {
        this.uploadTranscriptError = 'Please paste a transcript before uploading.'
        return
      }

      this.uploadingTranscript = true
      this.uploadTranscriptError = null

      try {
        await axios.patch(`/api/engagements/${this.engagement_id}/`, {
          zoomMeetingTranscription: this.uploadTranscriptText.trim()
        })

        // Refresh engagement data
        await this.fetchEngagementDetails()

        // Close modal and reset
        this.closeUploadTranscriptModal()

        // Auto-trigger transcript summarization
        this.summarizeTranscript()
      } catch (error) {
        console.error('Error uploading transcript:', error)
        this.uploadTranscriptError = error.response?.data?.message || 'Failed to upload transcript. Please try again.'
      } finally {
        this.uploadingTranscript = false
      }
    }
  },
  mounted() {
    this.fetchEngagementDetails()
    this.fetchEngagementChannels()
    this.fetchEngagementTypes()
    this.fetchEngagementTopics()
    this.fetchAttachments()
    this.autoSaveInterval = setInterval(this.updateEngagement, 30000)
  },
  beforeUnmount() {
    clearInterval(this.autoSaveInterval)
    if(!this.isDiscarded){
      this.updateEngagement()
    }
  },
  watch: {
  }
})
</script>

<style scoped>
/* Add any custom animations or additional styles here */
.fa-spin {
  animation: fa-spin 2s infinite linear;
}

@keyframes fa-spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Ensure proper hover states for nested groups */
.group:hover .group-hover\:opacity-100 {
  opacity: 1;
}
</style>
